import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiList, FiSearch, FiVideo, FiX } = FiIcons;

const STOP_WORDS = new Set(['a', 'an', 'and', 'are', 'can', 'do', 'for', 'how', 'i', 'in', 'is', 'it', 'me', 'my', 'of', 'on', 'the', 'this', 'to', 'we', 'what', 'where', 'with', 'you']);

const escapeFilterValue = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"');

const getSearchTokens = (value) => {
  const tokens = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token.length > 1 && !STOP_WORDS.has(token));
  return (tokens.length ? tokens : [String(value || '').trim()]).filter(Boolean).slice(0, 6);
};

const buildTokenFilter = (value, fields) => getSearchTokens(value)
  .map(token => {
    const safeToken = escapeFilterValue(token);
    return `(${fields.map(field => `${field} ~ "${safeToken}"`).join(' || ')})`;
  })
  .join(' && ');

const GlobalSearch = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ apps: [], videos: [], topics: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults({ apps: [], videos: [], topics: [] });
      setError('');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const appSearch = buildTokenFilter(term, ['app_name', 'app_slug']);
        const videoSearch = buildTokenFilter(term, ['title', 'description', 'keywords', 'tags', 'category']);
        const topicSearch = buildTokenFilter(term, ['name', 'description', 'tags']);

        const [appsResult, videosResult, topicsResult] = await Promise.all([
          apiCall(() => pb.collection('apps').getList(1, 5, {
            filter: appSearch,
            sort: 'app_name'
          })),
          apiCall(() => pb.collection('videos').getList(1, 6, {
            filter: `workflow_status != "archived" && ${videoSearch}`,
            sort: '-updated'
          })),
          apiCall(() => pb.collection('playlists').getList(1, 5, {
            filter: `workflow_status != "archived" && ${topicSearch}`,
            sort: '-updated'
          }))
        ]);

        if (cancelled) return;
        setResults({
          apps: appsResult.items || [],
          videos: videosResult.items || [],
          topics: topicsResult.items || []
        });
      } catch (searchError) {
        if (cancelled) return;
        console.error('Global Video Hub search failed:', searchError);
        setResults({ apps: [], videos: [], topics: [] });
        setError('Search is temporarily unavailable.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  const totalResults = results.apps.length + results.videos.length + results.topics.length;

  const goTo = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const clearSearch = () => {
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const ResultRow = ({ icon, eyebrow, title, description, onClick }) => (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-100 transition-colors"
    >
      <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={icon} className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{eyebrow}</p>
        <p className="text-sm font-semibold text-slate-900 truncate mt-0.5">{title}</p>
        {description && <p className="text-xs text-slate-500 truncate mt-0.5">{description}</p>}
      </div>
    </button>
  );

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative">
        <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          placeholder="Search Apps, videos, topics, descriptions, tags…"
          className="w-full pl-10 pr-24 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none shadow-sm"
          aria-label="Search across BPA Video Hub"
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {query && (
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={clearSearch} className="p-1 text-slate-400 hover:text-slate-900" aria-label="Clear search">
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          )}
          <span className="hidden sm:inline-flex px-2 py-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-500 bg-slate-50">Ctrl K</span>
        </div>
      </div>

      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>{loading ? 'Searching Video Hub…' : error || `${totalResults} result${totalResults === 1 ? '' : 's'}`}</span>
            <span>Admin search</span>
          </div>

          {!loading && !error && totalResults === 0 && (
            <div className="px-5 py-8 text-center">
              <SafeIcon icon={FiSearch} className="w-7 h-7 text-slate-300 mx-auto" />
              <p className="text-sm font-semibold text-slate-900 mt-3">No matches found</p>
              <p className="text-xs text-slate-500 mt-1">Try an App name, topic, video title, description, tag or keyword.</p>
            </div>
          )}

          {results.apps.length > 0 && (
            <div className="border-b border-slate-100">
              {results.apps.map(app => (
                <ResultRow key={`app-${app.id}`} icon={FiGrid} eyebrow="App" title={app.app_name} description={app.app_slug} onClick={() => goTo(`/apps/${app.id}/setup`)} />
              ))}
            </div>
          )}

          {results.topics.length > 0 && (
            <div className="border-b border-slate-100">
              {results.topics.map(topic => (
                <ResultRow key={`topic-${topic.id}`} icon={FiList} eyebrow="Topic / Playlist" title={topic.name} description={topic.description || 'Training topic'} onClick={() => goTo(`/playlists/${topic.id}`)} />
              ))}
            </div>
          )}

          {results.videos.length > 0 && (
            <div>
              {results.videos.map(video => (
                <ResultRow key={`video-${video.id}`} icon={FiVideo} eyebrow="Video" title={video.title} description={video.description || video.category || 'Training video'} onClick={() => goTo(`/videos/${video.id}`)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;